import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Kpi } from '../../components/ui/Kpi'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { ETICHETE_CIFRE } from '@b4l/shared'

interface Product {
  sku: string | null
  title: string
  variantTitle: string | null
  qtyTotal: number
  ordersCount: number
  uniqueCustomers: number
  revenue: string
  discountTotal: string
  firstOrder: string | null
  lastOrder: string | null
}

interface ProductsResp {
  days: number
  total: number
  /*
    Numarate cu codul ecranului Comenzi, pe aceeasi perioada — cifrele sunt identice acolo:
    plasate = „Comenzi (filtru)", esuate = „Comenzi esuate", clienti = „Clienti",
    revenueOrders = „Venit (RON)".
  */
  ordersPlaced: number
  ordersSold: number
  ordersFailed: number
  customers: number
  revenueOrders: string
  shipping: string
  products: Product[]
}

interface BreakdownResp {
  sku: string
  days: number
  bySegment: Array<{ segment: string; orders: number; revenue: string; qty: number }>
  byCity: Array<{ city: string | null; orders: number; revenue: string; qty: number }>
  byVoucher: Array<{ code: string; uses: number; revenue: string; qty: number }>
}

/** Scurtaturi: completeaza „De la" / „Până la"; intervalul ramane editabil. */
const PRESETS = [
  { days: 30, label: '30 zile' },
  { days: 90, label: '90 zile' },
  { days: 180, label: '6 luni' },
  { days: 365, label: '12 luni' },
  { days: 730, label: '24 luni' },
]

const SEGMENT_LABELS: Record<string, string> = {
  VIP: 'VIP',
  recurrent: 'Recurenți',
  active: 'Activi',
  at_risk: 'At-risk',
  reactivation: 'Reactivare',
  churned: 'Churned',
  one_time: 'O singură comandă',
  unknown: 'Nesegmentat',
}

type SortKey = 'sku' | 'title' | 'qty' | 'orders' | 'customers' | 'revenue' | 'discount'

/** Cifrele pornesc de la cea mai mare; textul, alfabetic. */
const SORT_IMPLICIT: Record<SortKey, 'asc' | 'desc'> = {
  sku: 'asc',
  title: 'asc',
  qty: 'desc',
  orders: 'desc',
  customers: 'desc',
  revenue: 'desc',
  discount: 'desc',
}

const valoareSort = (p: Product, k: SortKey): number | string => {
  switch (k) {
    case 'sku':
      return p.sku ?? ''
    case 'title':
      return p.title
    case 'qty':
      return p.qtyTotal
    case 'orders':
      return p.ordersCount
    case 'customers':
      return p.uniqueCustomers
    case 'revenue':
      return Number(p.revenue)
    case 'discount':
      return Number(p.discountTotal)
  }
}

/** Ziua de azi minus `zile`, ca `AAAA-LL-ZZ` in ora locala. */
function ziua(zile = 0): string {
  const d = new Date()
  d.setDate(d.getDate() - zile)
  const l = String(d.getMonth() + 1).padStart(2, '0')
  const z = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${l}-${z}`
}

/*
  Nicio paginare (Madalin, 10.10.2026): sunt cateva zeci de SKU-uri, nu mii, iar „1–10 din 13"
  ascundea trei produse dupa un click. Se cer toate deodata — plafonul rutei e 500.
*/
const LIMITA_PRODUSE = 500

const numar = (n: number) => n.toLocaleString('ro-RO')

export function ProduseShopifyPage() {
  const [dateFrom, setDateFrom] = useState(() => ziua(90))
  const [dateTo, setDateTo] = useState(() => ziua())
  const [sortKey, setSortKey] = useState<SortKey>('revenue')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [drillSku, setDrillSku] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const perioada = new URLSearchParams()
  if (dateFrom) perioada.set('dateFrom', dateFrom)
  if (dateTo) perioada.set('dateTo', dateTo)
  /* Fara nicio data, ruta ar cadea pe „ultimele 90 de zile" — pe ecran ar scrie altceva. */
  if (!dateFrom && !dateTo) perioada.set('days', '730')

  const query = useQuery<ProductsResp>({
    queryKey: ['shopify-products', perioada.toString()],
    queryFn: async () =>
      (
        await api.get<ProductsResp>(
          `/shopify/products-stats?${perioada.toString()}&limit=${LIMITA_PRODUSE}`,
        )
      ).data,
  })

  const exportProducts = async () => {
    try {
      setExporting(true)
      await downloadXlsx(`/shopify/exports/products?${perioada.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  const breakdownQuery = useQuery<BreakdownResp>({
    queryKey: ['shopify-product-breakdown', drillSku, perioada.toString()],
    queryFn: async () =>
      (
        await api.get<BreakdownResp>(
          `/shopify/products-stats/${encodeURIComponent(drillSku!)}/breakdown?${perioada.toString()}`,
        )
      ).data,
    enabled: drillSku !== null,
  })

  const total = query.data?.total ?? 0
  const totals = useMemo(
    () =>
      (query.data?.products ?? []).reduce(
        (acc, p) => {
          acc.revenue += Number(p.revenue)
          acc.discount += Number(p.discountTotal)
          acc.qty += p.qtyTotal
          return acc
        },
        { revenue: 0, discount: 0, qty: 0 },
      ),
    [query.data],
  )

  const products = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...(query.data?.products ?? [])].sort((a, b) => {
      const va = valoareSort(a, sortKey)
      const vb = valoareSort(b, sortKey)
      const cmp =
        typeof va === 'string' && typeof vb === 'string'
          ? va.localeCompare(vb, 'ro')
          : (va as number) - (vb as number)
      return cmp * dir
    })
  }, [query.data, sortKey, sortDir])

  const sorteaza = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortKey(k)
      setSortDir(SORT_IMPLICIT[k])
    }
  }
  const th = (k: SortKey, eticheta: string, dreapta = true) => (
    <Th dreapta={dreapta} activ={sortKey === k} directie={sortDir} onClick={() => sorteaza(k)}>
      {eticheta}
    </Th>
  )

  const azi = ziua()
  const presetActiv = (zile: number) => dateTo === azi && dateFrom === ziua(zile)
  const cifra = (n: number) => (query.isError ? '—' : n.toLocaleString('ro-RO'))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Performanță produse Shopify</h1>
        <Button variant="secondary" onClick={exportProducts} loading={exporting}>
          {!exporting && <Download className="h-4 w-4" />}
          Export XLSX
        </Button>
      </div>

      {/*
        Fila „Pe luni" a fost scoasa (Madalin, 10.10.2026): cu intervalul de date si sortarea,
        lista de produse raspunde si la „ce creste". Ruta `/products-stats/monthly` a ramas in API.
      */}
      <div className="space-y-4">
        <Card>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Field
                label="De la"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="w-40">
              <Field
                label="Până la"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div>
              <span className="block text-xs font-medium text-b4l-text-dim">Rapid</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {PRESETS.map((p) => (
                  <button
                    key={p.days}
                    onClick={() => {
                      setDateFrom(ziua(p.days))
                      setDateTo(azi)
                    }}
                    className={`rounded border px-2 py-1 text-xs ${
                      presetActiv(p.days)
                        ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                        : 'border-b4l-border bg-white hover:bg-b4l-bg'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Pe eroare, cifrele de sus devin „—". Un zero afisat arata ca un raspuns, si asta a fost
          exact problema: patru KPI-uri pe zero pareau un magazin fara vanzari. */}
        {/*
          Comenzile vandute, nu toate (Madalin, 10.10.2026): un colet refuzat nu e vanzare. Cele
          plasate si cele esuate stau dedesubt, cu aceleasi cifre ca pe ecranul Comenzi — cine
          compara cele doua ecrane vede de unde vine diferenta.
        */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
          <Kpi
            ton="comenzi"
            label="Comenzi vândute"
            value={cifra(query.data?.ordersSold ?? 0)}
            detaliu={
              query.data &&
              `din ${numar(query.data.ordersPlaced)} plasate · ${numar(query.data.ordersFailed)} eșuate`
            }
            explicatie="Comenzile care s-au transformat în vânzare. Eșuate = plecate la client, dar nevândute: colet refuzat sau neridicat, ori retur. Plasate și eșuate sunt aceleași cifre ca pe ecranul Comenzi."
          />
          <Kpi ton="comenzi" label="Produse" value={cifra(total)} />
          <Kpi ton="comenzi" label="Bucăți vândute" value={cifra(totals.qty)} />
          <Kpi
            ton="clienti"
            label="Clienți"
            value={cifra(query.data?.customers ?? 0)}
            explicatie="Clienți diferiți care au cumpărat în perioadă, numărați ca pe ecranul Comenzi: după contul Shopify, altfel după email, altfel după telefon."
          />
          <Kpi
            ton="venit"
            label="Venit produse (RON)"
            value={cifra(Math.round(totals.revenue))}
            detaliu={
              query.data &&
              `${ETICHETE_CIFRE.venitProduseShopify.detaliu} · cu transport: ${numar(Math.round(Number(query.data.revenueOrders)))}`
            }
            explicatie="Valoarea produselor vândute, după reduceri, cu TVA, fără transport, după data comenzii. „Cu transport” e tot ce s-a încasat pe comenzile vândute — aceeași cifră ca „Venit (RON)” de pe ecranul Comenzi."
          />
          <Kpi
            ton="discount"
            label="Discount acordat"
            value={cifra(Math.round(totals.discount))}
            explicatie="Reducerile de pe produsele vândute, inclusiv codurile de reducere de pe comandă, împărțite pe produse de Shopify."
          />
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                  {th('sku', 'SKU', false)}
                  {th('title', 'Produs', false)}
                  {th('qty', 'Bucăți')}
                  {th('orders', 'Comenzi')}
                  {th('customers', 'Clienți unici')}
                  {th('revenue', 'Venit (RON)')}
                  {th('discount', 'Discount (RON)')}
                  {/* % mix e venitul impartit la total: aceeasi ordine ca „Venit". */}
                  {th('revenue', '% mix')}
                </tr>
              </thead>
              <tbody>
                {query.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-2 py-6 text-center text-b4l-text-muted">
                      Se încarcă...
                    </td>
                  </tr>
                )}
                {/*
                EROAREA NU E O LISTĂ GOALĂ.

                Ruta a răspuns 500 de când a fost scrisă, iar ecranul spunea liniștit „Niciun
                produs vândut în perioadă". Nimeni n-a avut de unde să bănuiască un bug: cifra
                zero e un răspuns plauzibil. Mădălin, 29.09.2026: „ar avea nevoie ca acest tab să
                fie funcțional" — abia atunci s-a uitat cineva.

                O interfață care nu distinge „n-am date" de „n-am putut întreba" ascunde exact
                defectele care contează.
              */}
                {query.isError && (
                  <tr>
                    <td colSpan={8} className="px-2 py-6 text-center text-rose-700">
                      Nu am putut aduce datele. Statisticile nu sunt zero — pur și simplu n-au putut
                      fi calculate. Semnalează-mi.
                    </td>
                  </tr>
                )}
                {products.length === 0 && !query.isLoading && !query.isError && (
                  <tr>
                    <td colSpan={8} className="px-2 py-6 text-center text-b4l-text-muted">
                      Niciun produs vândut în perioadă
                    </td>
                  </tr>
                )}
                {products.map((p, i) => {
                  const rev = Number(p.revenue)
                  const pct = totals.revenue > 0 ? (rev / totals.revenue) * 100 : 0
                  return (
                    <tr
                      key={p.sku ?? i}
                      onClick={() => p.sku && setDrillSku(p.sku)}
                      className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                    >
                      <td className="px-2 py-1.5 font-mono text-xs">{p.sku ?? '-'}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-xs font-medium">{p.title}</div>
                        {p.variantTitle && (
                          <div className="text-[10px] text-b4l-text-muted">{p.variantTitle}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">{p.qtyTotal}</td>
                      <td className="px-2 py-1.5 text-right">{p.ordersCount}</td>
                      <td className="px-2 py-1.5 text-right">{p.uniqueCustomers}</td>
                      <td className="px-2 py-1.5 text-right font-medium">
                        {rev.toLocaleString('ro-RO', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-2 py-1.5 text-right text-b4l-red-dark">
                        {Number(p.discountTotal).toLocaleString('ro-RO', {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs">{pct.toFixed(1)}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Modal
        open={drillSku !== null}
        onClose={() => setDrillSku(null)}
        title={`Detalii produs: ${drillSku ?? ''}`}
        size="xl"
      >
        {breakdownQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {breakdownQuery.data && (
          <div className="space-y-4">
            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
                Pe segment client
              </div>
              <BreakdownTable
                rows={breakdownQuery.data.bySegment.map((r) => ({
                  label: SEGMENT_LABELS[r.segment] ?? r.segment,
                  orders: r.orders,
                  revenue: Number(r.revenue),
                  qty: r.qty,
                }))}
                totalRevenue={breakdownQuery.data.bySegment.reduce(
                  (s, r) => s + Number(r.revenue),
                  0,
                )}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
                Pe oraș (top 10)
              </div>
              <BreakdownTable
                rows={breakdownQuery.data.byCity.map((r) => ({
                  label: r.city ?? '(necunoscut)',
                  orders: r.orders,
                  revenue: Number(r.revenue),
                  qty: r.qty,
                }))}
                totalRevenue={breakdownQuery.data.byCity.reduce((s, r) => s + Number(r.revenue), 0)}
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">
                Cu voucher (top 10)
              </div>
              {breakdownQuery.data.byVoucher.length === 0 ? (
                <p className="text-xs text-b4l-text-muted">
                  Niciun voucher folosit pe acest produs
                </p>
              ) : (
                <BreakdownTable
                  rows={breakdownQuery.data.byVoucher.map((r) => ({
                    label: r.code,
                    orders: r.uses,
                    revenue: Number(r.revenue),
                    qty: r.qty,
                  }))}
                  totalRevenue={breakdownQuery.data.byVoucher.reduce(
                    (s, r) => s + Number(r.revenue),
                    0,
                  )}
                />
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function BreakdownTable({
  rows,
  totalRevenue,
}: {
  rows: Array<{ label: string; orders: number; revenue: number; qty: number }>
  totalRevenue: number
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-b4l-text-muted">Fără date</p>
  }
  return (
    <div className="overflow-x-auto rounded border border-b4l-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-b4l-border bg-b4l-bg-soft text-left">
            <th className="px-2 py-1.5">Categorie</th>
            <th className="px-2 py-1.5 text-right">Comenzi</th>
            <th className="px-2 py-1.5 text-right">Bucăți</th>
            <th className="px-2 py-1.5 text-right">Venit (RON)</th>
            <th className="px-2 py-1.5 text-right">% mix</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const pct = totalRevenue > 0 ? (r.revenue / totalRevenue) * 100 : 0
            return (
              <tr key={i} className="border-b border-b4l-border">
                <td className="px-2 py-1.5 font-medium">{r.label}</td>
                <td className="px-2 py-1.5 text-right">{r.orders}</td>
                <td className="px-2 py-1.5 text-right">{r.qty}</td>
                <td className="px-2 py-1.5 text-right">
                  {r.revenue.toLocaleString('ro-RO', { maximumFractionDigits: 0 })}
                </td>
                <td className="px-2 py-1.5 text-right">{pct.toFixed(1)}%</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Th({
  children,
  dreapta,
  activ,
  directie,
  onClick,
}: {
  children: ReactNode
  dreapta: boolean
  activ: boolean
  directie: 'asc' | 'desc'
  onClick: () => void
}) {
  return (
    <th
      className={`px-2 py-2 ${dreapta ? 'text-right' : ''}`}
      aria-sort={activ ? (directie === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex select-none items-center gap-1 hover:text-b4l-text ${
          activ ? 'text-b4l-blue-dark' : ''
        }`}
      >
        {children}
        <span className={activ ? '' : 'text-b4l-text-muted/50'}>
          {activ ? (directie === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  )
}
